<template>
    <div class="events-container">
        <div class="events-header">
            <h2>Events & Meetings</h2>
            <button @click="showAddEventModal = true" class="add-event-btn">
                Add New Event
            </button>
        </div>

        <div class="calendar-view">
            <!-- Calendar navigation -->
            <div class="calendar-nav">
                <button @click="previousMonth">&lt;</button>
                <h3>{{ currentMonthYear }}</h3>
                <button @click="nextMonth">&gt;</button>
            </div>

            <!-- Events list -->
            <div class="events-list">
                <div v-for="event in currentMonthEvents" :key="event.id" class="event-card">
                    <div class="event-date">
                        {{ formatDate(event.date) }}
                    </div>
                    <div class="event-details">
                        <h4>{{ event.title }}</h4>
                        <p class="event-time">{{ formatTime(event.startTime) }} - {{ formatTime(event.endTime) }}</p>
                        <p class="event-location">{{ event.location }}</p>
                        <p class="event-description">{{ event.description }}</p>
                    </div>
                    <div class="event-actions">
                        <button @click="editEvent(event)" class="edit-btn">Edit</button>
                        <button @click="deleteEvent(event.id)" class="delete-btn">Delete</button>
                    </div>
                </div>
            </div>
        </div>

        <!-- Add/Edit Event Modal -->
        <div v-if="showAddEventModal" class="modal">
            <div class="modal-content">
                <h3>{{ editingEvent ? 'Edit Event' : 'Add New Event' }}</h3>
                <form @submit.prevent="saveEvent">
                    <div class="form-group">
                        <label>Title</label>
                        <input v-model="eventForm.title" type="text" required>
                    </div>
                    <div class="form-group">
                        <label>Date</label>
                        <input v-model="eventForm.date" type="date" required>
                    </div>
                    <div class="form-row">
                        <div class="form-group">
                            <label>Start Time</label>
                            <input v-model="eventForm.startTime" type="time" required>
                        </div>
                        <div class="form-group">
                            <label>End Time</label>
                            <input v-model="eventForm.endTime" type="time" required>
                        </div>
                    </div>
                    <div class="form-group">
                        <label>Location</label>
                        <input v-model="eventForm.location" type="text" required>
                    </div>
                    <div class="form-group">
                        <label>Description</label>
                        <textarea v-model="eventForm.description" rows="3"></textarea>
                    </div>
                    <div class="modal-actions">
                        <button type="button" @click="closeModal">Cancel</button>
                        <button type="submit" class="primary">{{ editingEvent ? 'Update' : 'Add' }} Event</button>
                    </div>
                </form>
            </div>
        </div>
    </div>
</template>

<script>
import { ref, computed, onMounted } from 'vue';
import { format, parseISO, startOfMonth, endOfMonth, addMonths, subMonths } from 'date-fns';
import { eventService } from '../services/eventService';

export default {
    name: 'EventsCalendar',
    setup() {
        const currentDate = ref(new Date());
        const events = ref([]);
        const showAddEventModal = ref(false);
        const editingEvent = ref(null);
        const eventForm = ref({
            title: '',
            date: '',
            startTime: '',
            endTime: '',
            location: '',
            description: ''
        });

        const currentMonthYear = computed(() => {
            return format(currentDate.value, 'MMMM yyyy');
        });

        const currentMonthEvents = computed(() => {
            const start = startOfMonth(currentDate.value);
            const end = endOfMonth(currentDate.value);
            return events.value
                .filter(event => {
                    const eventDate = parseISO(event.date);
                    return eventDate >= start && eventDate <= end;
                })
                .sort((a, b) => parseISO(a.date) - parseISO(b.date));
        });

        const formatDate = (date) => format(parseISO(date), 'EEEE, MMMM do');
        const formatTime = (time) => {
            const [hours, minutes] = time.split(':');
            return new Date(2000, 0, 1, hours, minutes).toLocaleTimeString([], { 
                hour: 'numeric', 
                minute: '2-digit'
            });
        };

        const previousMonth = () => {
            currentDate.value = subMonths(currentDate.value, 1);
        };

        const nextMonth = () => {
            currentDate.value = addMonths(currentDate.value, 1);
        };

        const closeModal = () => {
            showAddEventModal.value = false;
            editingEvent.value = null;
            eventForm.value = {
                title: '',
                date: '',
                startTime: '',
                endTime: '',
                location: '',
                description: ''
            };
        };

        const editEvent = (event) => {
            editingEvent.value = event;
            eventForm.value = { ...event };
            showAddEventModal.value = true;
        };

        const saveEvent = async () => {
            try {
                if (editingEvent.value) {
                    // Update existing event
                    await eventService.updateEvent(editingEvent.value.id, eventForm.value);
                } else {
                    // Create new event
                    await eventService.createEvent(eventForm.value);
                }
                closeModal();
                // Reload events
                loadEvents();
            } catch (error) {
                console.error('Error saving event:', error);
            }
        };

        const deleteEvent = async (eventId) => {
            if (confirm('Are you sure you want to delete this event?')) {
                try {
                    await eventService.deleteEvent(eventId);
                    loadEvents();
                } catch (error) {
                    console.error('Error deleting event:', error);
                }
            }
        };

        const loadEvents = async () => {
            try {
                const response = await eventService.getEvents();
                events.value = response;
            } catch (error) {
                console.error('Error loading events:', error);
            }
        };

        onMounted(() => {
            loadEvents();
        });

        return {
            currentMonthYear,
            currentMonthEvents,
            showAddEventModal,
            eventForm,
            editingEvent,
            formatDate,
            formatTime,
            previousMonth,
            nextMonth,
            saveEvent,
            editEvent,
            deleteEvent,
            closeModal
        };
    }
};
</script>

<style scoped>
.events-container {
    padding: 2rem;
}

.events-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 2rem;
}

.calendar-nav {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 2rem;
}

.event-card {
    background: white;
    border-radius: 8px;
    padding: 1.5rem;
    margin-bottom: 1rem;
    box-shadow: 0 2px 4px rgba(0,0,0,0.1);
    display: flex;
    gap: 2rem;
}

.event-date {
    min-width: 150px;
    font-weight: bold;
}

.event-details {
    flex: 1;
}

.event-time {
    color: #666;
    margin: 0.5rem 0;
}

.event-location {
    color: #666;
    font-style: italic;
}

.event-actions {
    display: flex;
    gap: 0.5rem;
}

.form-row {
    display: flex;
    gap: 1rem;
}

.form-row .form-group {
    flex: 1;
}

/* Reuse modal styles from AdminPanel */
</style> 